import { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { useHistory } from "react-router-dom";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { ItemModelV2 } from "../../../../../erp/inventory/models/item_model";
import {
  AddressModel,
  CreateFoCSTPayload,
  ItemRow,
  ItemWarehouseModel,
  ReturnableDestinationType,
} from "../../model/warehouse_outward_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import { UserModel } from "../../../../../context_providers/auth/user_model";
import UserSearchFieldV2 from "../../../orders/order_create/customer_section/customer/user_search_widgetV2";
import {
  CreateFoCStockTransfer,
  GetAvailableQty,
  GetCustomerBillingAddresses,
  GetCustomerShippingAddresses,
  GetItemWAC,
} from "../../warehouse_module_repo";
import { formatAddress } from "../../warehouse_helper";
import { invalidStateWarning } from "../../../../../utils/states_data";
import LoadingWidget from "../../../../../context_providers/modal/loader";
import RequiredMark from "../../../../new_components/common/required_mark";
import { validateWarehouseAddress } from "../../../../../utils/warehouse_util";
import { getMissingAddressFields } from "../../../../../utils/location_util";
import ItemSearchFieldV2 from "../../../../../erp/inventory/items/item_search_fieldv2";

export default function FoCSTForm(): JSX.Element {
  const history = useHistory();
  const { showToast } = useToast();

  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );
  const [customer, setCustomer] = useState<UserModel>();
  const [billingAddress, setBillingAddress] = useState<AddressModel | null>(
    null,
  );
  const [shippingAddress, setShippingAddress] = useState<AddressModel | null>(
    null,
  );
  const [billingOptions, setBillingOptions] = useState<AddressModel[]>([]);
  const [shippingOptions, setShippingOptions] = useState<AddressModel[]>([]);

  const [dispatchDate, setDispatchDate] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [items, setItems] = useState<ItemRow[]>([]);
  const [error, setError] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ItemModelV2 | null>(null);
  const [purpose, setPurpose] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [logisticPartner, setLogisticPartner] = useState<string>("");
  const [trackingId, setTrackingId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [availableQtyMap, setAvailableQtyMap] = useState<
    Record<string, number>
  >({});

  const availableItemCodes = useMemo(() => {
    return Object.entries(availableQtyMap)
      .filter(([, qty]) => (qty ?? 0) > 0)
      .map(([itemCode]) => itemCode);
  }, [availableQtyMap]);

  useEffect(() => {
    setItems([]);
  }, [fromWarehouse?.id]);

  useEffect(() => {
    const fetchAllQty = async () => {
      if (!fromWarehouse?.id) return;

      try {
        setLoading(true);

        const res = await GetAvailableQty(fromWarehouse.id);

        if (!res?.success || !res?.data?.item_data) {
          setAvailableQtyMap({});
          return;
        }

        const map: Record<string, number> = {};

        res.data.item_data.forEach((item: any) => {
          map[item.item_code] = item.available_units ?? 0;
        });

        setAvailableQtyMap(map);
      } catch (err) {
        console.error(err);
        setAvailableQtyMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllQty();
  }, [fromWarehouse?.id]);

  const fetchAvailableQty = (itemId: string): number => {
    return availableQtyMap[itemId] ?? 0;
  };

  const handleFromWarehouseSelect = (warehouse: WarehouseModel | null) => {
    if (!warehouse) {
      setFromWarehouse(null);
      setItems([]);
      setSelectedItem(null);
      setError("");
      return;
    }

    try {
      validateWarehouseAddress(warehouse.address!);
      setFromWarehouse(warehouse);
      setItems([]);
      setSelectedItem(null);
      setError("");
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message,
      });
      setFromWarehouse(null);
    }
  };

  // ---------- fetch customer billing/shipping ----------

  useEffect(() => {
    const load = async () => {
      if (!customer?.uid) return;

      const [billingRes, shippingRes] = await Promise.all([
        GetCustomerBillingAddresses(customer.uid),
        GetCustomerShippingAddresses(customer.uid),
      ]);

      if (billingRes?.success)
        setBillingOptions(
          (billingRes.data?.billing_profiles || []).map((p: any) => p.address),
        );

      if (shippingRes?.success)
        setShippingOptions(
          (shippingRes.data?.billing_profiles || []).map((p: any) => p.address),
        );
    };

    load();
  }, [customer]);

  // ---------- helpers ----------

  const fetchItemWac = async (
    itemCode: string,
  ): Promise<{ value: number; isFetched: boolean }> => {
    try {
      const res = await GetItemWAC(itemCode);

      if (res?.success && typeof res?.data?.wac_value === "number") {
        const value = res.data.wac_value;
        return { value, isFetched: value > 0 };
      }
      return { value: 0, isFetched: false };
    } catch {
      return { value: 0, isFetched: false };
    }
  };

  // const fetchAvailableQty = async (
  //   warehouseId: string,
  //   itemId: string,
  // ): Promise<number> => {
  //   try {
  //     const res = await GetAvailableQty(warehouseId);

  //     if (!res?.success || !res?.data?.item_data) return 0;

  //     const match = res.data.item_data.find((i: any) => i.item_id === itemId);
  //     return match?.available_units ?? 0;
  //   } catch {
  //     return 0;
  //   }
  // };

  const addItem = async () => {
    if (!fromWarehouse) {
      showToast({ type: ToastType.warning, text: "Select source warehouse" });
      return;
    }

    if (!selectedItem) return;

    const exists = items.some((i) => i.itemCode === selectedItem.item_code);
    if (exists) return setError("Item already added");

    // Check if item has HSN code
    if (
      !selectedItem.tax_detail?.hsn_sac ||
      selectedItem.tax_detail.hsn_sac.trim() === ""
    ) {
      showToast({
        type: ToastType.error,
        text: "Cannot add item without HSN code",
      });
      return;
    }

    const availableQty = fetchAvailableQty(selectedItem.item_code!);
    if (!availableQty)
      return showToast({ type: ToastType.error, text: "No stock" });

    const wac = await fetchItemWac(selectedItem.item_code || "");
    if (!wac.isFetched) {
      showToast({
        type: ToastType.warning,
        text: "WAC not found. Enter unit price manually.",
      });
    }

    setItems((p) => [
      ...p,
      {
        itemCode: selectedItem.item_code || "",
        itemName: selectedItem.name || "",
        quantity: 0,
        unitPrice: wac.value,
        isWacFetched: wac.isFetched,
        itemUnit: selectedItem.unit?.formal_name || "",
        itemHsn: selectedItem.tax_detail?.hsn_sac || "",
        subcategory: selectedItem.subcategory || "",
        availableQty,
      },
    ]);

    setSelectedItem(null);
    setError("");
  };

  const removeItem = (i: number) =>
    setItems((p) => p.filter((_, x) => x !== i));

  const totalValue = items.reduce((t, i) => t + i.quantity * i.unitPrice, 0);

  // ---------- submit ----------

  const handleCreate = async () => {
    if (!fromWarehouse) return setError("Select warehouse");
    if (!dispatchDate) return setError("Select expected dispatch date");
    if (!purpose.trim()) return setError("Enter purpose");
    if (!description.trim()) return setError("Enter description");

    if (!contactName.trim()) return setError("Please enter contact name");

    if (!String(contactNumber).trim())
      return setError("Please enter contact number");

    if (String(contactNumber).replace(/\D/g, "").length < 10)
      return setError("Contact number must be at least 10 digits");
    if (items.length === 0) return setError("Add at least one item");
    if (items.some((i) => i.quantity <= 0))
      return setError("All items must have quantity greater than 0");
    if (items.some((i) => i.unitPrice <= 0))
      return setError("Item can't be added with wac value 0");

    if (!customer?.uid || !billingAddress || !shippingAddress) {
      return setError("Select customer, billing address, and shipping address");
    }

    const focBillingMissing = getMissingAddressFields(billingAddress, {
      relaxForNonIndia: true,
    });
    if (focBillingMissing.length)
      return setError(
        `Billing address is missing ${focBillingMissing.join(", ")}. Please edit the customer address and try again.`,
      );
    const focShippingMissing = getMissingAddressFields(shippingAddress, {
      relaxForNonIndia: true,
    });
    if (focShippingMissing.length)
      return setError(
        `Shipping address is missing ${focShippingMissing.join(", ")}. Please edit the customer address and try again.`,
      );

    // Check if all items have HSN codes
    if (items.some((i) => !i.itemHsn || i.itemHsn.trim() === ""))
      return setError("All items must have HSN codes before creating");

    const payload: CreateFoCSTPayload = {
      from_warehouse_id: fromWarehouse.id,
      from_warehouse_name: fromWarehouse.name,

      destination_type: ReturnableDestinationType.CUSTOMER,

      customer_name: `${customer?.first_name} ${customer?.last_name}`,
      customer_id: customer?.uid,
      billing_address: billingAddress ?? undefined,
      shipping_address: shippingAddress ?? undefined,

      expected_dispatch_date: new Date(dispatchDate),
      contact_name: contactName,
      contact_number: contactNumber,
      items: items.map(
        (i): ItemWarehouseModel => ({
          inventoryItemCode: i.itemCode,
          itemName: i.itemName,
          requestedQty: i.quantity,
          itemPrice: i.unitPrice,
          subCategory: i.subcategory,
          itemHSN: i.itemHsn,
          itemUnit: i.itemUnit,
          tax_detail: {
            igst: 0,
            cgst: 0,
            sgst: 0,
          },
          gst_applicable: false,
        }),
      ),

      description,
      purpose,
      shipping_details: {
        logistic_partner: logisticPartner.trim() || undefined,
        tracking_id: trackingId.trim() || undefined,
      },
    };

    try {
      setLoading(true);
      const res = await CreateFoCStockTransfer(payload);

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "ST Creation Failed",
        });
        return;
      }

      showToast({
        type: ToastType.success,
        text: "FoC ST Created Successfully",
      });
      history.goBack();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ---------- UI ----------

  return (
    <div id="warehouse-stock-transfer-foc-form" className="p-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">
            From Warehouse <RequiredMark />
          </label>
          <WarehouseSearchField
            id="warehouse-stock-transfer-foc-form-from-warehouse"
            value={fromWarehouse || undefined}
            onSelect={handleFromWarehouseSelect}
          />
        </div>

        <div className="w-full  gap-4">
          <label className="text-sm font-medium">
            Customer <RequiredMark />
          </label>
          <UserSearchFieldV2
            id="warehouse-stock-transfer-foc-form-customer"
            value={customer as any}
            onSelect={setCustomer}
          />
        </div>
      </div>

      {/* customer rows */}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <select
          id="warehouse-stock-transfer-foc-form-billing-address"
          className="h-8 border rounded-md px-3"
          value={billingAddress?.id || ""}
          onChange={(e) => {
            const selected = billingOptions.find(
              (a) => a.id === e.target.value,
            );
            setBillingAddress(selected || null);
          }}
        >
          <option value="">Billing Address</option>

          {billingOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {formatAddress(a)}
            </option>
          ))}
        </select>

        <select
          id="warehouse-stock-transfer-foc-form-shipping-address"
          className="h-8 border rounded-md px-3"
          value={shippingAddress?.id || ""}
          onChange={(e) => {
            const selected = shippingOptions.find(
              (a) => a.id === e.target.value,
            );
            setShippingAddress(selected || null);
          }}
        >
          <option value="">Shipping Address</option>

          {shippingOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {formatAddress(a)}
            </option>
          ))}
        </select>
        {invalidStateWarning(shippingAddress) && (
          <p className="sm:col-span-2 text-xs text-red-600">
            {invalidStateWarning(shippingAddress)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">
            Expected Dispatch Date *
          </label>

          <input
            id="warehouse-stock-transfer-foc-form-dispatch-date"
            type="date"
            value={dispatchDate}
            onChange={(e) => setDispatchDate(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
        <div>
          <label className="text-sm font-medium">
            Contact Name <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-foc-form-contact-name"
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
        <div>
          <label className="text-sm font-medium">
            Contact Number <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-foc-form-contact-number"
            type="text"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">
            Purpose <RequiredMark />
          </label>
          <input
            id="warehouse-stock-transfer-foc-form-purpose"
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>

        <div>
          <label className="text-sm font-medium">
            Description <RequiredMark />{" "}
          </label>
          <input
            id="warehouse-stock-transfer-foc-form-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Logistic Partner</label>
          <input
            id="warehouse-stock-transfer-foc-form-logistic-partner"
            type="text"
            value={logisticPartner}
            onChange={(e) => setLogisticPartner(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
            placeholder="Enter logistic partner"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Tracking ID</label>
          <input
            id="warehouse-stock-transfer-foc-form-tracking-id"
            type="text"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            className="w-full h-8 border rounded-md px-3"
            placeholder="Enter tracking ID"
          />
        </div>
      </div>
      {/* items UI unchanged */}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Item Details</h2>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <ItemSearchFieldV2
              id="warehouse-stock-transfer-foc-form-item-search"
              disabled={!fromWarehouse}
              value={selectedItem || undefined}
              placeholder="Search item"
              subcategory={[
                "Raw Materials",
                "Components",
                "Others",
                "JobWork",
                "Semi finished goods",
                "Logistics and Transportation",
              ]}
              onSelect={setSelectedItem}
            />
          </div>
          <button
            id="warehouse-stock-transfer-foc-form-add-item-btn"
            onClick={addItem}
            disabled={loading}
            className={`px-4 bg-blue-600 text-white rounded-md flex items-center transition-all ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            <Icon path={mdiPlus} size={0.8} />
          </button>
        </div>

        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={index}
              id={`warehouse-stock-transfer-foc-form-item-row-${item.itemCode ?? index}`}
              className="p-4 border rounded-xl bg-gray-50 space-y-3"
            >
              <div className="font-medium">
                {item.itemName} - {item.itemCode}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  id={`warehouse-stock-transfer-foc-form-item-row-${item.itemCode ?? index}-quantity`}
                  type="number"
                  min={1}
                  max={item.availableQty}
                  value={item.quantity}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value > item.availableQty) return;

                    setItems((prev) =>
                      prev.map((it, i) =>
                        i === index ? { ...it, quantity: value } : it,
                      ),
                    );
                  }}
                  className="h-10 border rounded-md px-3"
                  placeholder="Quantity"
                />

                <input
                  id={`warehouse-stock-transfer-foc-form-item-row-${item.itemCode ?? index}-unit-price`}
                  type="number"
                  min={0}
                  value={item.unitPrice}
                  disabled={item.isWacFetched ?? item.unitPrice > 0}
                  onChange={(e) => {
                    const updated = [...items];
                    updated[index].unitPrice = Number(e.target.value);
                    setItems(updated);
                  }}
                  className="h-10 border rounded-md px-3"
                  placeholder="Unit Price"
                />
              </div>

              <div className="flex justify-between items-center">
                <p className="text-xs text-green-700 font-medium">
                  Available Units: {item.availableQty}
                </p>

                <button
                  id={`warehouse-stock-transfer-foc-form-item-row-${item.itemCode ?? index}-remove-btn`}
                  onClick={() => removeItem(index)}
                  className="text-red-600"
                >
                  <Icon path={mdiTrashCanOutline} size={0.8} />
                </button>
              </div>
            </div>
          ))}

          {items.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">
              No items added
            </p>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</p>
      )}

      <div className="border-t px-6 py-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="font-semibold">
          Total: Rs. {totalValue.toLocaleString()}
        </div>
        <div className="flex gap-3">
          <button
            id="warehouse-stock-transfer-foc-form-cancel-btn"
            onClick={() => history.goBack()}
            disabled={loading}
            className="border px-6 py-2 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            id="warehouse-stock-transfer-foc-form-submit-btn"
            onClick={handleCreate}
            disabled={loading}
            className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {loading ? "Creating..." : "Create FoC ST"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
